import {
    Component,
    lazy,
    Suspense,
    useCallback,
    useEffect,
    useState,
} from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { interferenceVisualizationProvider, formatEntityName } from './interferenceVisualization';
import type { RuleVisualizationProps, VisualizationSequence } from './types';

const MiniBimScene = lazy(() => import('./MiniBimScene'));

const PROVIDERS = {
    interference: interferenceVisualizationProvider,
};

function usePrefersReducedMotion(): boolean {
    const getPreference = () => typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const [reduced, setReduced] = useState(getPreference);

    useEffect(() => {
        if (typeof window.matchMedia !== 'function') return undefined;
        const media = window.matchMedia('(prefers-reduced-motion: reduce)');
        const onChange = () => setReduced(media.matches);
        media.addEventListener?.('change', onChange);
        return () => media.removeEventListener?.('change', onChange);
    }, []);

    return reduced;
}

interface SceneErrorBoundaryProps {
    children: ReactNode;
    fallback: ReactNode;
}

interface SceneErrorBoundaryState {
    failed: boolean;
}

class SceneErrorBoundary extends Component<SceneErrorBoundaryProps, SceneErrorBoundaryState> {
    state: SceneErrorBoundaryState = { failed: false };

    static getDerivedStateFromError(): SceneErrorBoundaryState {
        return { failed: true };
    }

    componentDidCatch(error: Error, info: ErrorInfo) {
        console.error('Rule visualization could not be rendered.', error, info);
    }

    render() {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}

function SceneFallback({ sequence }: { sequence: VisualizationSequence }) {
    return (
        <div className="irv-scene-fallback" role="status">
            <strong>Visual preview unavailable</strong>
            <span>{sequence.summary}</span>
        </div>
    );
}

export function RuleVisualization({ ruleKind, draft }: RuleVisualizationProps) {
    const provider = PROVIDERS[ruleKind];
    const sequence = provider.buildSequence(draft);
    const reducedMotion = usePrefersReducedMotion();
    const [paused, setPaused] = useState(reducedMotion);
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        setActiveIndex(0);
        setPaused(reducedMotion);
    }, [sequence.key, reducedMotion]);

    const onScenarioChange = useCallback((index: number) => setActiveIndex(index), []);
    const activeScenario = sequence.scenarios[activeIndex] ?? sequence.scenarios[0];
    const pairLabel = sequence.firstEntity && sequence.secondEntity
        ? `${formatEntityName(sequence.firstEntity)} vs ${formatEntityName(sequence.secondEntity)}`
        : 'Waiting for entity selection';

    return (
        <aside className="irv-panel" aria-labelledby="rule-visualization-title">
            <header className="irv-header">
                <div>
                    <span className="irv-eyebrow">Live rule explanation</span>
                    <h2 id="rule-visualization-title">Rule visualization</h2>
                </div>
                <div className="irv-header-actions">
                    <span className="irv-pair-badge">{pairLabel}</span>
                    {sequence.ready && (
                        <button
                            type="button"
                            className="irv-playback-button"
                            onClick={() => setPaused((current) => !current)}
                            aria-label={paused ? 'Play rule visualization' : 'Pause rule visualization'}
                        >
                            <span aria-hidden="true">{paused ? '▶' : 'Ⅱ'}</span>
                            {paused ? 'Play' : 'Pause'}
                        </button>
                    )}
                </div>
            </header>

            {!sequence.ready ? (
                <div className="irv-empty-state">
                    <div className="irv-empty-cube" aria-hidden="true" />
                    <strong>Build the geometric relationship</strong>
                    <p>{sequence.prompt}</p>
                </div>
            ) : (
                <>
                    <div className="irv-scene-shell" aria-hidden="true">
                        <SceneErrorBoundary key={sequence.key} fallback={<SceneFallback sequence={sequence} />}>
                            <Suspense fallback={<div className="irv-scene-loading">Preparing BIM scene…</div>}>
                                <MiniBimScene
                                    sequence={sequence}
                                    paused={paused}
                                    onScenarioChange={onScenarioChange}
                                />
                            </Suspense>
                        </SceneErrorBoundary>
                        {activeScenario && (
                            <span className={`irv-status-badge irv-status-${activeScenario.tone}`}>
                                {activeScenario.title}
                            </span>
                        )}
                    </div>

                    {activeScenario && (
                        <div className="irv-current-state" aria-hidden="true">
                            <span className={`irv-state-marker irv-state-marker-${activeScenario.tone}`} />
                            <div>
                                <strong>{activeScenario.title}</strong>
                                <p>{activeScenario.description}</p>
                            </div>
                        </div>
                    )}

                    <p className="irv-sr-only">
                        {sequence.summary} The visualization cycles through: {' '}
                        {sequence.scenarios.map((scenario) => `${scenario.title}: ${scenario.description}`).join(' ')}
                    </p>
                </>
            )}
        </aside>
    );
}
